import React, { useContext } from "react";
import { StaffContext } from "../../../../../Context.jsx";
import mut from "../../../hooks/mut.ts";
import useCTX from "../../../hooks/useCTX.ts";
import { UserElement } from '../types';
import { Usedbo } from "../../../hooks/useDBO.ts";
import Text from "../../../components/Text.tsx";
import Lang from "../../../components/Lang.jsx";
import Select from "../../../components/Select.tsx";
import Dialog from "../../../components/Dialog.jsx";
import { UserPlus } from "lucide-react";

/**
 * NewStaffDialog — Dialog for adding a new staff member.
 * Uses custom Dialog.jsx (no MUI). Validates name/login uniqueness
 * against the server before allowing submission.
 *
 * @author Forge (opencode + glm-5.1)
 * @date 2026-04-16
 * @version 2.0
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible
 * @param {function} props.setOpen - Toggle dialog visibility
 * @param {Usedbo<UserElement>} props.myStaff - Staff collection to add the new member to
 * @returns {React.ReactElement}
 */
type NewStaffDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  myStaff: Usedbo<UserElement>;
};

type CheckUserResponse = {
  name: boolean;
  login: boolean;
};

type Check = {
  name: string | undefined;
  login: string | undefined;
};

export default function NewStaffDialog({ open, setOpen, myStaff }: NewStaffDialogProps) {
  const { update } = useContext(StaffContext);
  const { user } = useCTX();
  const [staff, setStaff] = React.useState({
    name: "",
    login: "",
    password: generatePassword(),
    salaryType: "Monthly",
    salary: 0,
    reportsTo: "",
    centerId: ""
  });
  const [errors, setErrors] = React.useState<CheckUserResponse>({ name: false, login: false });

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStaff({
        name: "",
        login: "",
        password: generatePassword(),
        salaryType: "Monthly",
        salary: 0,
        reportsTo: "",
        centerId: "",
      });
      setErrors({ name: false, login: false });
    }, 1000);
  };

  const enterStaff = async () => {
    const nstaff = { ...staff, reportsTo: user._id, centerId: user.center };
    const res = await mut("/users/new", nstaff);
    update(res);
    myStaff.add(res);
    handleClose();
  };

  const checkUserLogin = async (check: Check) => {
    const res = await mut<CheckUserResponse>("/check-new-user", check);
    setErrors(res);
  };

  const isDisabled = !staff.name || !staff.login || !staff.password || errors.name || errors.login;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onConfirm={enterStaff}
      confirmDisabled={isDisabled}
      title="addANewStaffMember"
      confirmLabel="addStaff"
      titleIcon={<UserPlus size={18} />}
      size="md"
    >
      <div className="flex flex-col gap-3">
        <Text
          value={staff.name}
          setValue={(name: string) => {
            setStaff({ ...staff, name });
            debounce(() => checkUserLogin({ login: staff.login, name }), 1000)();
          }}
          error={errors.name}
          label="Name"
          fullWidth
        />

        <Text
          value={staff.login}
          setValue={(login: string) => {
            setStaff(prev => ({ ...prev, login }));
            debounce(() => checkUserLogin({ login, name: staff.name }), 1000)();
          }}
          label="Login"
          type="text"
          fullWidth
          endAdornment="@htexplore.vn"
          helperText={errors.login ? "There is already a staff member with this login" : ""}
          autoComplete="off"
        />

        <Text
          value={staff.password}
          setValue={(password: string) => setStaff({ ...staff, password })}
          label="Password"
          autoComplete="new-password"
        />

        <Select
          options={["Monthly", "Hourly"]}
          value={staff.salaryType}
          setValue={(salaryType: string) => setStaff({ ...staff, salaryType })}
          label="Type of salary"
        />

        <Text
          value={staff.salary}
          setValue={(salary: number) => setStaff({ ...staff, salary })}
          label="Salary"
          vnd
        />
      </div>
    </Dialog>
  );
}

function debounce<T extends (...args: any[]) => void>(
  func: T,
  timeout = 300
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

/**
 * Generate a random 8-character alphanumeric password.
 * @returns {string} 8-char password using a-z and 0-9
 */
function generatePassword(): string {
  const chars = "abcdefghijkmnpqrstuvwxyz23456789";
  let pw = "";
  for (let i = 0; i < 8; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}
